import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  getTenants,
  getScoreSummary,
  DEFAULT_POSTURE_RANGE,
  getMFASummary,
  getAssessmentSummary,
  getRiskyUsersSummary,
  getMFAGlobalAdmins,
} from '../services/api'

export type RiskLabel = 'High' | 'Med' | 'Low'
export type StatusKey = 'crit' | 'warn' | 'ok'

export interface FleetTenantRow {
  id: string
  name: string
  domain: string
  mfa: number // % strong-MFA compliance
  secure: number // % secure score
  assess: number // % assessment score
  risky: number // active risky users
  admins: number // global admins without strong MFA
  crit: number // actionable critical count
  status: StatusKey
  risk: RiskLabel
}

export interface FleetKpis {
  tenants: number
  mfaCoverage: number // % of tenants with strong-MFA >= 90
  avgSecure: number
  criticalIssues: number
  criticalTenants: number
  totalAdmins: number
  totalRisky: number
}

export interface FleetData {
  rows: FleetTenantRow[] // sorted by risk (worst first)
  kpis: FleetKpis
  isLoading: boolean
  isError: boolean
}

function statusFor(secure: number, crit: number): StatusKey {
  if (secure < 55 || crit > 0) return 'crit'
  if (secure < 80) return 'warn'
  return 'ok'
}
function riskFor(status: StatusKey): RiskLabel {
  return status === 'crit' ? 'High' : status === 'warn' ? 'Med' : 'Low'
}

export function useFleet(): FleetData {
  const tenants = useQuery({ queryKey: ['tenants'], queryFn: () => getTenants() })
  const scores = useQuery({ queryKey: ['score-summary', DEFAULT_POSTURE_RANGE], queryFn: () => getScoreSummary(DEFAULT_POSTURE_RANGE) })
  const mfa = useQuery({ queryKey: ['mfa-summary'], queryFn: getMFASummary })
  const assess = useQuery({ queryKey: ['assessment-summary'], queryFn: getAssessmentSummary })
  const risky = useQuery({ queryKey: ['risky-summary'], queryFn: getRiskyUsersSummary })
  const admins = useQuery({ queryKey: ['mfa-global-admins'], queryFn: getMFAGlobalAdmins })

  const isLoading = [tenants, scores, mfa, assess, risky, admins].some((q) => q.isLoading)
  const isError = [tenants, scores, mfa, assess, risky, admins].some((q) => q.isError)

  // Recompute only when the underlying query data changes — this hook is mounted
  // by the Sidebar (and WorkspaceSwitcher) on every page, so an unmemoized
  // reduce/map/sort would re-run on every render/navigation.
  const { rows, kpis } = useMemo(() => {
  const secureBy = new Map((scores.data?.tenantScores ?? []).map((s) => [s.tenantId, s.percentage]))
  const mfaBy = new Map((mfa.data?.tenantBreakdown ?? []).map((m) => [m.tenantId, m.compliancePercent]))
  const assessBy = new Map((assess.data?.entries ?? []).map((a) => [a.tenantId, a.score ?? 0]))
  const riskyBy = new Map((risky.data ?? []).map((r) => [r.tenantId, { total: r.total, high: r.high }]))
  // Global admins lacking strong MFA (no MFA, or SMS/phone-only), counted per tenant.
  const adminBy = new Map<string, number>()
  for (const a of admins.data ?? []) {
    const strong = a.mfaEnabled && !a.mfaMethods.every((m) => m === 'Phone (SMS/Call)' || m === 'None')
    if (!strong) adminBy.set(a.tenantId, (adminBy.get(a.tenantId) ?? 0) + 1)
  }

  const rows: FleetTenantRow[] = (tenants.data ?? []).map((t) => {
    const secure = Math.round(secureBy.get(t.id) ?? 0)
    const mfaPct = Math.round(mfaBy.get(t.id) ?? 0)
    const assessPct = Math.round(assessBy.get(t.id) ?? 0)
    const riskyN = riskyBy.get(t.id)?.total ?? 0
    const riskyHigh = riskyBy.get(t.id)?.high ?? 0
    const adminsN = adminBy.get(t.id) ?? 0
    const crit = adminsN + riskyHigh
    const status = statusFor(secure, crit)
    return {
      id: t.id, name: t.name, domain: t.primaryDomain || t.tenantId,
      mfa: mfaPct, secure, assess: assessPct, risky: riskyN, admins: adminsN, crit,
      status, risk: riskFor(status),
    }
  })

  rows.sort((a, b) => b.crit - a.crit || a.secure - b.secure)

  const n = rows.length || 1
  const kpis: FleetKpis = {
    tenants: rows.length,
    mfaCoverage: Math.round((rows.filter((r) => r.mfa >= 90).length / n) * 100),
    avgSecure: Math.round(rows.reduce((s, r) => s + r.secure, 0) / n),
    criticalIssues: rows.reduce((s, r) => s + r.crit, 0),
    criticalTenants: rows.filter((r) => r.crit > 0).length,
    totalAdmins: rows.reduce((s, r) => s + r.admins, 0),
    totalRisky: rows.reduce((s, r) => s + r.risky, 0),
  }

    return { rows, kpis }
  }, [tenants.data, scores.data, mfa.data, assess.data, risky.data, admins.data])

  return { rows, kpis, isLoading, isError }
}

// Color helpers matching the mock's thresholds.
export const bandColor = (v: number) => (v >= 80 ? 'text-success' : v >= 55 ? 'text-warning' : 'text-danger')
export const bandBar = (v: number) => (v >= 80 ? 'bg-success' : v >= 55 ? 'bg-warning' : 'bg-danger')
export const statusDot = (s: StatusKey) => (s === 'crit' ? 'bg-danger' : s === 'warn' ? 'bg-warning' : 'bg-success')
